import { useRef } from 'react';
import { Award, Target, TrendingUp, Users } from 'lucide-react';
import { motion, useInView } from 'motion/react';
import { ImageWithFallback } from './figma/ImageWithFallback';

export function About() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const values = [
    {
      icon: Target,
      title: 'Misión',
      description: 'Proporcionar infraestructura confiable para operaciones industriales y logísticas de alto estándar.',
    },
    {
      icon: Award,
      title: 'Excelencia',
      description: 'Operación disciplinada, servicio consistente y atención profesional en cada punto de contacto.',
    },
    {
      icon: TrendingUp,
      title: 'Crecimiento',
      description: 'Acompañamos la expansión de nuestros clientes con soluciones escalables y previsibles.',
    },
    {
      icon: Users,
      title: 'Compromiso',
      description: 'Relación cercana con cada empresa, con soporte ágil y enfoque en continuidad operativa.',
    },
  ];

  return (
    <section id="about" className="py-20 md:py-32 bg-white border-t border-slate-200" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="rounded-[1.5rem] overflow-hidden border border-slate-200 shadow-sm bg-slate-50">
              <div className="aspect-[4/3]">
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1619070284836-e850273d69ac?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlJTIwbG9naXN0aWNzfGVufDF8fHx8MTc3NDg4MzUyNHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                  alt="Warehouse interior"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="absolute -bottom-6 -right-6 rounded-2xl bg-slate-950 text-white px-6 py-5 shadow-lg border border-slate-800">
              <div className="text-4xl font-semibold leading-none">15+</div>
              <div className="text-xs uppercase tracking-[0.22em] text-slate-400 mt-2">Años de experiencia</div>
            </div>

            <div className="absolute -top-6 -left-6 w-28 h-28 rounded-full bg-slate-100 border border-slate-200 -z-10" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full border border-slate-200 bg-slate-50 text-slate-700 mb-6">
              Sobre Nosotros
            </div>

            <h2 className="text-4xl md:text-5xl text-slate-950 mb-6 leading-tight tracking-tight">
              Infraestructura sólida para
              <span className="block text-slate-600 font-medium">operaciones que exigen orden y escala</span>
            </h2>

            <p className="text-lg text-slate-600 mb-5 leading-relaxed">
              Free Zone Logistic Park es un desarrollo industrial y logístico pensado para empresas que necesitan
              estabilidad, conectividad y una operación estructurada en un entorno profesional.
            </p>

            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              Combinamos ubicación estratégica, infraestructura moderna y servicios de soporte para crear una base
              confiable sobre la que nuestros clientes puedan crecer con claridad.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 18 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.2 + index * 0.08 }}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-5 shadow-sm hover:shadow-md transition-shadow"
                  role="article"
                  aria-label={value.title}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
                      <value.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-slate-950 font-semibold mb-1">{value.title}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">{value.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
